import type {
  MessageTask,
  CreateGuestLinkInput,
  GuestLink,
  GuestPermission,
  HuddleGuest,
  ApiToken,
  Canvas,
  CanvasShare,
  CanvasShareKind,
  List,
  AuditEntry,
  ChannelWithMembership,
  CreateOutgoingWebhookInput,
  CreateSlashCommandInput,
  CreateTokenInput,
  CustomEmoji,
  EmojiUsage,
  FileAttachment,
  HuddleChatInput,
  OutgoingWebhook,
  UpdateOutgoingWebhookInput,
  RetentionPolicy,
  SlashCommand,
  UpdateRetentionInput,
  Huddle,
  PhoneStats,
  HuddleEventInput,
  HuddleToken,
  HistoryResponse,
  Message,
  NotifyLevel,
  AiSettings,
  OpenRouterModel,
  Transcript,
  TranscriptHit,
  CanvasSearchHit,
  ChannelRecap,
  DeviceSession,
  TranscriptState,
  Presence,
  PublicUser,
  ReactionSummary,
  RichTextDoc,
  Draft,
  GifResult,
  ScheduledMessage,
  UpdateProfileInput,
  Team,
  UserGroup,
  PhoneLine,
  PhoneLineCandidates,
  PhoneLineInput,
  SipSettings,
  SipSettingsInput,
  PhoneLeg,
  MyPhoneLine,
  MessageRevision,
  PhoneContact,
  PhoneIdentity,
  TtMatch,
  CallNoteResult,
  CallNoteState,
  CustomerSystem,
  TtNumber,
  PhoneRecent,
  CallableLine,
  AdminServiceOverview,
  CreateHuddlePollInput,
  HuddlePoll,
  ServiceFields,
  ServiceLevel,
  ServiceStatus,
  UpdateServiceFieldsInput,
  UpdateServiceLevelInput,
  StartBreakoutInput,
  NotificationDiagnosis,
  TimerActionInput,
  UpdateBreakoutInput,
} from '@avslack/shared';
import type {
  ApiError,
  ReminderRepeatKind,
  SavedItem,
  SavedStatus,
  UpdateSavedInput,
} from '@avslack/shared';
import {
  SESSION_REFRESH_HEADER,
  USAGE_CLIENT_HEADER,
  type LoadTestInput,
  type LoadTestRun,
  type UsageStats,
} from '@avslack/shared';
import { apiBase, authHeaders, getDesktopToken, isDesktop, setDesktopToken } from './runtime';

/**
 * Kozos API-hivo. Ket dolog kotelezo minden hivasnal:
 *  - `credentials: 'include'` - a session httpOnly cookie-ban van, JS nem latja
 *  - 401 eseten egysegesen a /login-ra dobunk
 */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(apiBase() + path, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      // Desktopban Bearer token, bongeszoben ures (ott a suti dolgozik).
      ...authHeaders(),
      // Honnan jon a keres - a hasznalati statisztika kliens szerinti bontasahoz.
      [USAGE_CLIENT_HEADER]: isDesktop() ? 'desktop' : 'web',
      ...init.headers,
    },
  });

  /*
    A MEGUJITOTT munkamenet atvetele.

    A szerver hasznalatra megujitja a munkamenetet, hogy senkit ne leptessen ki
    munka kozben. Bongeszoben ehhez nem kell tennunk semmit - a suti frissul
    magatol. Az asztali appban viszont a felulet tarolja a tokent, ezert a
    frisset fejlecben kapjuk: ha nem vennenk at, az app a REGI tokennel menne
    tovabb, es a megujitas eppen ott nem erne semmit, ahol nincs suti.
  */
  const frissToken = response.headers.get(SESSION_REFRESH_HEADER);
  if (frissToken && getDesktopToken()) setDesktopToken(frissToken);

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : undefined;

  if (!response.ok) {
    const error = (payload as ApiError | undefined)?.error;
    throw new ApiRequestError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? `HTTP ${response.status}`,
    );
  }

  return payload as T;
}

const post = <T>(path: string, body?: unknown) =>
  apiFetch<T>(path, {
    method: 'POST',
    body: body === undefined ? undefined : JSON.stringify(body),
  });

const patch = <T>(path: string, body: unknown) =>
  apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) });

const put = <T>(path: string, body: unknown) =>
  apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) });

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface SessionInfo {
  user: PublicUser;
  /** Az elso belepes kotelezo lepese - a felulet ezt nem engedi atugrani. */
  mustChangePassword: boolean;
  /**
   * A telefonos ugyfelrendszer (TT / ProjectFlow / Salesforce / egyik sem).
   * Regi szerver vagy offline gyorsitotar eseten hianyozhat.
   */
  customerSystem?: CustomerSystem;
}

export const getMe = () => apiFetch<SessionInfo>('/auth/me');

export const getAuthConfig = () =>
  apiFetch<{
    authEnabled: boolean;
    serverUrl: string;
    minPasswordLength: number;
    /** Belepes Microsoft-fiokkal (versenyaudit B1) be van-e allitva. */
    entraEnabled?: boolean;
  }>('/auth/config');

/** Az azonosito lehet e-mail cim VAGY felhasznalonev - a szerver donti el. */
export const login = (identifier: string, password: string) =>
  post<{ user: PublicUser; mustChangePassword: boolean; token?: string }>('/auth/login', {
    identifier,
    password,
    // Desktopban a tokent a torzsben is kerjuk: ott nincs suti.
    client: isDesktop() ? 'desktop' : 'web',
  });

export const changePassword = (currentPassword: string, newPassword: string) =>
  post<{ ok: boolean; user: PublicUser }>('/auth/change-password', {
    currentPassword,
    newPassword,
  });

export const forgotPassword = (email: string) => post<{ ok: boolean }>('/auth/forgot', { email });

export const resetPassword = (token: string, password: string) =>
  post<{ ok: boolean; user: PublicUser }>('/auth/reset', { token, password });

export const logout = () => post<{ ok: boolean }>('/auth/logout');

/** Rovid eletu, egyszer hasznalhato jegy a WebSocket-nyitashoz. */
export const getWsTicket = () => post<{ ticket: string }>('/auth/ws-ticket').then((r) => r.ticket);

// ─── Felhasznalok ─────────────────────────────────────────────────────────────

export const listUsers = () =>
  apiFetch<{ users: PublicUser[]; presence: Record<string, Presence> }>('/api/users');

// ─── Csatornak ────────────────────────────────────────────────────────────────

export const listChannels = () =>
  apiFetch<{ channels: ChannelWithMembership[] }>('/api/channels').then((r) => r.channels);

/**
 * Egy csatorna teljes tagsaga.
 *
 * A csatornalista szandekosan NEM hozza magaval a tagokat (egy nagy publikus
 * csatornanal az minden oldalbetoltesnel szaz azonositot jelentene) - ahol
 * tenyleg kell, ott kerjuk le kulon.
 */
export const listChannelMembers = (channelId: string) =>
  apiFetch<{ memberIds: string[] }>(`/api/channels/${channelId}/members`).then((r) => r.memberIds);

// ─── Egyedi emoji (admin) ────────────────────────────────────────────────────

export const listAdminEmoji = () =>
  apiFetch<{ emoji: CustomEmoji[] }>('/api/admin/emoji').then((r) => r.emoji);

export const addCustomEmoji = (name: string, fileId: string) =>
  post<{ ok: boolean }>('/api/admin/emoji', { name, fileId });

export const deleteCustomEmoji = (id: string) =>
  apiFetch<{ ok: boolean }>(`/api/admin/emoji/${id}`, { method: 'DELETE' });

// ─── Asztali alkalmazas ──────────────────────────────────────────────────────

export interface DesktopAsset {
  id: number;
  name: string;
  size: number;
  platform: 'windows' | 'macos' | 'egyéb';
}

export interface DesktopRelease {
  version: string;
  publishedAt: string | null;
  assets: DesktopAsset[];
}

export const getDesktopRelease = () =>
  apiFetch<{ enabled: boolean; release: DesktopRelease | null }>('/api/desktop/latest');

/** GIF-kereses a sajat proxynkon at (a Giphy-kulcs a szerveren marad). */
export const searchGifs = (query: string) =>
  apiFetch<{ gifs: GifResult[] }>(`/api/gifs?q=${encodeURIComponent(query)}`).then((r) => r.gifs);

/** A workspace egyedi emojijai - mindenki lathatja, felvinni admin tud. */
export const listCustomEmoji = () =>
  apiFetch<{ emoji: CustomEmoji[] }>('/api/emoji').then((r) => r.emoji);

export const listFrequentEmoji = () =>
  apiFetch<{ emoji: EmojiUsage[] }>('/api/emoji/frequent').then((r) => r.emoji);

export const addChannelMembers = (channelId: string, userIds: string[]) =>
  post<{ added: string[] }>(`/api/channels/${channelId}/members`, { userIds });

/**
 * Vendeg behivasa MASIK cegbol - pontos e-mail cimmel.
 *
 * A nevsorok nem latszanak at a cegek kozott, ezert itt nincs kereses: aki
 * behiv valakit, az tudja a cimet.
 */
export const inviteGuestToChannel = (channelId: string, email: string, fullHistory: boolean) =>
  post<{ added: boolean; user: PublicUser }>(`/api/channels/${channelId}/guests`, {
    email,
    fullHistory,
  });

export const removeChannelMember = (channelId: string, userId: string) =>
  apiFetch<{ removed: boolean }>(`/api/channels/${channelId}/members/${userId}`, {
    method: 'DELETE',
  });

/**
 * A sajat jegyzetek beszelgetese - igeny szerint keletkezik.
 *
 * Idempotens: masodszorra ugyanazt adja vissza, nem egy ujabbat, ezert
 * nyugodtan hivhato minden megnyitaskor.
 */
export const openNotes = () =>
  post<{ channel: ChannelWithMembership }>('/api/channels/notes').then((r) => r.channel);

/** A csapatok, amikben a felhasznalo mozoghat - a cegvaltohoz. */
export const listTeams = () => apiFetch<{ teams: Team[] }>('/api/teams').then((r) => r.teams);

export const browseChannels = () =>
  apiFetch<{ channels: ChannelWithMembership[] }>('/api/channels/browse').then((r) => r.channels);

export const createChannel = (input: {
  name: string;
  kind: 'public' | 'private';
  topic?: string;
}) => post<{ channel: ChannelWithMembership }>('/api/channels', input).then((r) => r.channel);

export const joinChannel = (channelId: string) =>
  post<{ channel: ChannelWithMembership }>(`/api/channels/${channelId}/join`).then(
    (r) => r.channel,
  );

export const leaveChannel = (channelId: string) =>
  post<{ left: boolean }>(`/api/channels/${channelId}/leave`);

export const updateChannel = (
  channelId: string,
  input: {
    name?: string;
    topic?: string | null;
    purpose?: string | null;
    isArchived?: boolean;
    /** Csak `public` es `private` kozott - DM-et nem lehet atalakitani. */
    kind?: 'public' | 'private';
    postPolicy?: 'all' | 'admins';
  },
) => patch<{ channel: ChannelWithMembership }>(`/api/channels/${channelId}`, input);

export const updateMembership = (
  channelId: string,
  input: { isStarred?: boolean; isMuted?: boolean; isHidden?: boolean; notifyLevel?: NotifyLevel },
) =>
  patch<{ channel: ChannelWithMembership }>(`/api/channels/${channelId}/membership`, input).then(
    (r) => r.channel,
  );

export const openDm = (userIds: string[]) =>
  post<{ channel: ChannelWithMembership; created: boolean }>('/api/dm', { userIds });

/**
 * Az oldalsav sajat sorrendje.
 *
 * A TELJES sorrendet kuldjuk, nem egyetlen elem uj helyet: egy huzas utan
 * ugyis minden erintett elem szama valtozik.
 */
export const reorderChannels = (channelIds: string[]) =>
  put<{ ok: true }>('/api/channels/order', { channelIds });

export const markChannelRead = (channelId: string, seq: number) =>
  post<{ lastReadSeq: number }>(`/api/channels/${channelId}/read`, { seq });

/** Olvatlanna jeloles a `seq` sorszamu uzenettol kezdve. */
export const markChannelUnread = (channelId: string, seq: number) =>
  post<{ lastReadSeq: number }>(`/api/channels/${channelId}/unread`, { seq });

// ─── Uzenetek ─────────────────────────────────────────────────────────────────

export interface HistoryParams {
  beforeSeq?: number;
  afterSeq?: number;
  aroundSeq?: number;
  limit?: number;
}

export const getMessages = (channelId: string, params: HistoryParams = {}) => {
  const query = new URLSearchParams();
  if (params.beforeSeq !== undefined) query.set('beforeSeq', String(params.beforeSeq));
  if (params.afterSeq !== undefined) query.set('afterSeq', String(params.afterSeq));
  if (params.aroundSeq !== undefined) query.set('aroundSeq', String(params.aroundSeq));
  query.set('limit', String(params.limit ?? 50));
  return apiFetch<HistoryResponse>(`/api/channels/${channelId}/messages?${query.toString()}`);
};

export const sendMessage = (
  channelId: string,
  input: {
    text: string;
    clientMsgId: string;
    parentId?: string | null;
    body?: RichTextDoc;
    fileIds?: string[];
    isImportant?: boolean;
    isTask?: boolean;
  },
) =>
  post<{ message: Message; deduplicated: boolean }>(`/api/channels/${channelId}/messages`, input);

/** FELADAT-jeloles (utolag is) es kipipalas. */
export const setMessageTask = (messageId: string, isTask: boolean) =>
  post<{ task: MessageTask | null }>(`/api/messages/${messageId}/task`, { isTask });

export const setMessageTaskDone = (messageId: string, done: boolean) =>
  post<{ task: MessageTask | null }>(`/api/messages/${messageId}/task-done`, { done });

export const listChannelTasks = (channelId: string) =>
  apiFetch<{ open: Message[]; done: Message[] }>(`/api/channels/${channelId}/tasks`);

/** A fontos-jeloles utolagos allitasa. Kivant ertek megy at, nem valto. */
export const setMessageImportant = (messageId: string, isImportant: boolean) =>
  post<{ isImportant: boolean }>(`/api/messages/${messageId}/important`, { isImportant });

export const editMessage = (messageId: string, text: string, body?: RichTextDoc | undefined) =>
  patch<{ message: Message }>(`/api/messages/${messageId}`, {
    text,
    ...(body ? { body } : {}),
  }).then((r) => r.message);

export const deleteMessage = (messageId: string) =>
  apiFetch<{ deleted: boolean }>(`/api/messages/${messageId}`, { method: 'DELETE' });

/** Ugyanaz a hivas teszi fel es veszi le - az ismetelt kattintas nem hibazik. */
export const toggleReaction = (messageId: string, emoji: string) =>
  post<{ reactions: ReactionSummary[]; added: boolean }>(`/api/messages/${messageId}/reactions`, {
    emoji,
  });

export const getThread = (messageId: string) =>
  apiFetch<{ parent: Message; replies: Message[] }>(`/api/messages/${messageId}/thread`);

export const getMessage = (messageId: string) =>
  apiFetch<{ message: Message | null }>(`/api/messages/${messageId}`).then((r) => r.message);

/** Kituzes valtasa - a csatorna minden tagja latja. */
export const togglePin = (messageId: string) =>
  post<{ isPinned: boolean }>(`/api/messages/${messageId}/pin`);

/** Mentes valtasa - szemelyes. */
export const toggleSaved = (messageId: string) =>
  post<{ isSaved: boolean }>(`/api/messages/${messageId}/save`);

export const getPinned = (channelId: string) =>
  apiFetch<{ messages: Message[] }>(`/api/channels/${channelId}/pins`).then((r) => r.messages);

/** A mentett elemek egy allapot-fule, a fulek darabszamaval (U7). */
export const getSaved = (status: SavedStatus) =>
  apiFetch<{ items: SavedItem[]; counts: Record<SavedStatus, number> }>(
    `/api/saved?status=${status}`,
  );

export const updateSaved = (messageId: string, input: UpdateSavedInput) =>
  apiFetch<{ ok: boolean }>(`/api/saved/${messageId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });

// ─── Profil, piszkozat, utemezes, csoportok ───────────────────────────────────

export const updateProfile = (input: UpdateProfileInput) =>
  patch<{ user: PublicUser }>('/api/users/me', input).then((r) => r.user);

export const listDrafts = () => apiFetch<{ drafts: Draft[] }>('/api/drafts').then((r) => r.drafts);

export const saveDraft = (channelId: string, input: { text: string; body?: RichTextDoc }) =>
  apiFetch<{ saved: boolean }>(`/api/channels/${channelId}/draft`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });

/** Elonezeti kep (PDF elso oldala, video kockaja) a feltolto bongeszojebol - K3. */
export const uploadFilePreview = (fileId: string, image: Blob) =>
  apiFetch<{ file: FileAttachment }>(`/api/files/${fileId}/preview`, {
    method: 'POST',
    body: image,
    headers: { 'content-type': image.type || 'image/jpeg' },
  }).then((r) => r.file);

/** Rovid eletu, kozvetlen tarolo-link a PDF-nezonek. */
export const getFileLink = (fileId: string) =>
  apiFetch<{ url: string }>(`/api/files/${fileId}/link`).then((r) => r.url);

export const listScheduled = () =>
  apiFetch<{ scheduled: ScheduledMessage[] }>('/api/scheduled').then((r) => r.scheduled);

export const scheduleMessage = (
  channelId: string,
  input: {
    text: string;
    body?: RichTextDoc;
    sendAt: string;
    isImportant?: boolean;
    repeat?: { kind: ReminderRepeatKind; days?: number[]; until?: string | null };
  },
) =>
  post<{ scheduled: ScheduledMessage }>(`/api/channels/${channelId}/scheduled`, input).then(
    (r) => r.scheduled,
  );

export const cancelScheduled = (scheduledId: string) =>
  apiFetch<{ canceled: boolean }>(`/api/scheduled/${scheduledId}`, { method: 'DELETE' });

export const listGroups = () =>
  apiFetch<{ groups: UserGroup[] }>('/api/groups').then((r) => r.groups);

/**
 * Csoport letrehozasa - a nev CEGEN BELUL egyedi.
 *
 * A `handle` az emlitesben hasznalt nev (`@fejlesztok`), es kesobb nem
 * valtoztathato: ha atirnank, a mar elkuldott uzenetekben allo emlites masra
 * mutatna, mint amire irtak.
 */
export const createGroup = (input: {
  handle: string;
  name: string;
  description?: string | null;
  memberIds?: string[];
}) => post<{ group: UserGroup }>('/api/groups', input).then((r) => r.group);

export const updateGroup = (
  groupId: string,
  valtozas: { name?: string; description?: string | null; memberIds?: string[] },
) => patch<{ group: UserGroup }>(`/api/groups/${groupId}`, valtozas).then((r) => r.group);

export const deleteGroup = (groupId: string) =>
  apiFetch<{ deleted: boolean }>(`/api/groups/${groupId}`, { method: 'DELETE' });

// ─── Integraciok ──────────────────────────────────────────────────────────────

export const triggerInteraction = (messageId: string, actionId: string) =>
  post<{ ok: boolean }>('/api/interactions', { messageId, actionId });

export const listCommands = () =>
  apiFetch<{ commands: SlashCommand[] }>('/api/commands').then((r) => r.commands);

export interface CommandMention {
  kind: 'user' | 'group';
  id: string;
  label: string;
}

export const invokeCommand = (channelId: string, text: string, mentions: CommandMention[] = []) =>
  post<{ ok: boolean; pending: boolean; reminderId?: string | null }>('/api/commands/invoke', {
    channelId,
    text,
    mentions,
  });

// ─── Admin ────────────────────────────────────────────────────────────────────

export interface AdminStats {
  users: number;
  bots: number;
  channels: number;
  messages: number;
  files: number;
  tokens: number;
  onlineNow: number;
}

export interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  /** Csak a kereseshez: a nevsorban nem latszik. Botnal es regi fioknal `null`. */
  username: string | null;
  /** Csak a kereseshez: a nevsorban nem latszik. */
  nickname: string | null;
  /** Nevjegyzek-adatok (AD2). */
  title: string | null;
  department: string | null;
  phone: string | null;
  extension: string | null;
  /** A szerep a SAJAT csapatan belul ertendo. */
  role: 'owner' | 'admin' | 'member' | 'guest';
  /** Mindent lat es szerkeszt, minden csapatban. Kulon tengely a szereptol. */
  isSuperadmin: boolean;
  /** A telefonos ugyfelrendszer felulirasa; `null` = a cege alapertelmezese. */
  customerSystem: CustomerSystem | null;
  teamId: string;
  teamName: string;
  isActive: boolean;
  isBot: boolean;
  lastSeenAt: string | null;
  createdAt: string;
  mustChangePassword: boolean;
  hasLoggedIn: boolean;
}

export const updateUserDirectory = (
  userId: string,
  input: {
    title: string | null;
    department: string | null;
    phone: string | null;
    extension: string | null;
  },
) =>
  apiFetch<{ user: PublicUser }>(`/api/admin/users/${userId}/directory`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });

/** A `teamId` egy ceg azonositoja, vagy `all` - ez utobbi csak szuperadminnak. */
/** Hasznalati statisztika - a felhasznalonkenti bontast csak szuperadmin kapja. */
export const getUsageStats = (teamId: string, days: number) =>
  apiFetch<{ stats: UsageStats }>(
    `/api/admin/usage?teamId=${encodeURIComponent(teamId)}&days=${days}`,
  ).then((r) => r.stats);

/** Terheleses proba - csak szuperadmin. */
export const listLoadTestRuns = () =>
  apiFetch<{ runs: LoadTestRun[]; running: boolean }>('/api/admin/loadtest/runs');
export const startLoadTest = (input: LoadTestInput) =>
  apiFetch<{ runId: string }>('/api/admin/loadtest', {
    method: 'POST',
    body: JSON.stringify(input),
  });
export const cleanupLoadTest = () =>
  apiFetch<{ users: number; messages: number }>('/api/admin/loadtest/cleanup', { method: 'POST' });

export const getAdminStats = (teamId: string) =>
  apiFetch<{ stats: AdminStats }>(`/api/admin/stats?teamId=${encodeURIComponent(teamId)}`).then(
    (r) => r.stats,
  );

export const listAdminUsers = () =>
  apiFetch<{ users: AdminUser[] }>('/api/admin/users').then((r) => r.users);

export const inviteUser = (input: {
  email: string;
  displayName: string;
  /** A masik belepesi azonosito. Ures = az e-mail elejebol keszul. */
  username?: string;
  nickname?: string;
  title?: string;
  role?: AdminUser['role'];
  /** Melyik cegbe kerul. Csak a szuperadmin adhat meg mast, mint a sajatja. */
  teamId?: string;
}) =>
  post<{ userId: string; tempPassword: string; mailSent: boolean; mailError: string | null }>(
    '/api/admin/users',
    input,
  );

// ─── Bejelentkezett eszkozok (versenyaudit B2) ───────────────────────────────

export const listMySessions = () =>
  apiFetch<{ sessions: DeviceSession[] }>('/api/sessions').then((r) => r.sessions);

export const revokeMySession = (sessionId: string) =>
  apiFetch<{ ok: true }>(`/api/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });

export const revokeMyOtherSessions = () =>
  post<{ revoked: number }>('/api/sessions/revoke-others', {});

/** Admin: a kolléga MINDEN eszkoze kilep (a fiok marad). */
export const revokeUserSessions = (userId: string) =>
  post<{ revoked: number }>(`/api/admin/users/${userId}/sessions/revoke`, {});

export const resetUserPassword = (userId: string) =>
  post<{ tempPassword: string; mailSent: boolean; mailError: string | null }>(
    `/api/admin/users/${userId}/reset-password`,
  );

export const setUserRole = (userId: string, role: AdminUser['role']) =>
  patch<{ ok: boolean }>(`/api/admin/users/${userId}/role`, { role });

/** Szuperadmin jog adasa/visszavonasa - csak szuperadmin teheti. */
export const setSuperadmin = (userId: string, isSuperadmin: boolean) =>
  apiFetch<{ ok: boolean }>(`/api/admin/users/${userId}/superadmin`, {
    method: 'PATCH',
    body: JSON.stringify({ isSuperadmin }),
  });

// ─── ProjectFlow (feladatkezelo) ──────────────────────────────────────────────

export interface ProjectflowStatus {
  /** Be van-e allitva a kiszolgalon ES bekapcsolva a cegednel. */
  enabled: boolean;
  linked: boolean;
  linkedAt: string | null;
}

export interface ProjectflowProject {
  id: string;
  name: string;
  team: { id: string; name: string } | null;
}

export interface ProjectflowColumn {
  id: string;
  name: string;
  is_done: boolean;
}

export interface ProjectflowSuggestion {
  title: string;
  description: string;
}

export const getProjectflowStatus = () => apiFetch<ProjectflowStatus>('/api/projectflow/status');

export const unlinkProjectflow = () =>
  apiFetch<{ ok: true }>('/api/projectflow/link', { method: 'DELETE' });

export const listProjectflowProjects = () =>
  apiFetch<{ projects: ProjectflowProject[] }>('/api/projectflow/projects').then((r) => r.projects);

export const listProjectflowColumns = (projectId: string) =>
  apiFetch<{ columns: ProjectflowColumn[] }>(
    `/api/projectflow/projects/${encodeURIComponent(projectId)}/columns`,
  ).then((r) => r.columns);

/** Egy uzenetbol egy feladat nyersanyaga, a visszamutato hivatkozassal. */
export const suggestProjectflowTasks = (messageId: string) =>
  post<{ suggestions: ProjectflowSuggestion[]; permalink: string }>('/api/projectflow/suggest', {
    messageId,
  });

/**
 * Az atirat atadasa a ProjectFlow-nak.
 *
 * Nem hoz letre feladatot: a masik oldal eltarolja a szoveget, es visszaad egy
 * cimet, ahol a kollega atnezi es finomitja a javaslatokat.
 */
export const submitProjectflowTranscript = (huddleId: string) =>
  post<{ url: string }>('/api/projectflow/transcript', { huddleId });

export const createProjectflowTasks = (input: {
  projectId: string;
  columnName: string;
  permalink?: string;
  tasks: ProjectflowSuggestion[];
}) =>
  post<{ created: { id: string; title: string }[]; error: string | null }>(
    '/api/projectflow/tasks',
    input,
  );

export interface AdminTeam {
  id: string;
  name: string;
  slug: string;
  isActive: boolean;
  /** Latszik-e a ceg embereinek a ProjectFlow-integracio. */
  projectflowEnabled: boolean;
  /** A telefonos ugyfelrendszer alapertelmezese. */
  customerSystem: CustomerSystem;
  /** Hany AKTIV tagja van. Ebbol latszik, hogy egy uj ceg meg ures. */
  memberCount: number;
  createdAt: string;
}

/** A ceg telefonos ugyfelrendszerenek alapertelmezese (ceg adminja / szuperadmin). */
export const setTeamCustomerSystem = (teamId: string, customerSystem: CustomerSystem) =>
  patch<{ ok: true }>(`/api/admin/teams/${teamId}/customer-system`, { customerSystem });

/** A felhasznalo felulirasa; `null` = a cege alapertelmezese. */
export const setUserCustomerSystem = (userId: string, customerSystem: CustomerSystem | null) =>
  patch<{ ok: true }>(`/api/admin/users/${userId}/customer-system`, { customerSystem });

export const listAdminTeams = () =>
  apiFetch<{ teams: AdminTeam[] }>('/api/admin/teams').then((r) => r.teams);

export const createTeam = (input: { name: string; slug: string }) =>
  post<{ team: { id: string; name: string; slug: string } }>('/api/admin/teams', input).then(
    (r) => r.team,
  );

/**
 * Ceg-beallitasok modositasa.
 *
 * A rovid nev SZANDEKOSAN nem szerepel: egyedi, es kesobb linkekben is all -
 * egy atnevezes csendben elrontana minden korabbi hivatkozast.
 */
export const updateTeam = (
  teamId: string,
  valtozas: { name?: string; isActive?: boolean; projectflowEnabled?: boolean },
) => patch<{ team: AdminTeam }>(`/api/admin/teams/${teamId}`, valtozas).then((r) => r.team);

/**
 * Athelyezes masik cegbe - csak szuperadmin.
 *
 * A regi ceg CSATORNAIBAN bent marad (vendegkent), a CSOPORTJAIBOL kikerul,
 * a szerepe `member`-re all vissza, es bekerul az uj ceg altalanos
 * csatornajaba. A valasz megmondja, hany csoportbol kerult ki.
 */
export const moveUserToTeam = (userId: string, teamId: string) =>
  patch<{ ok: boolean; toroltCsoportok: number }>(`/api/admin/users/${userId}/team`, { teamId });

export const setUserActive = (userId: string, isActive: boolean) =>
  patch<{ ok: boolean }>(`/api/admin/users/${userId}/active`, { isActive });

export const listTokens = () =>
  apiFetch<{ tokens: ApiToken[] }>('/api/admin/tokens').then((r) => r.tokens);

/** Szuperadmin: a token hivasinditasa minden cegre (ProjectFlow). A titok nem valtozik. */
export const updateTokenAllTeamsCalls = (id: string, allTeamsCalls: boolean) =>
  patch<{ token: ApiToken | null }>(`/api/admin/tokens/${id}`, { allTeamsCalls });

export const createToken = (input: CreateTokenInput) =>
  post<{ token: string; webhookUrl: string | null; created: ApiToken | null }>(
    '/api/admin/tokens',
    input,
  );

export const revokeToken = (id: string) =>
  apiFetch<{ ok: boolean }>(`/api/admin/tokens/${id}`, { method: 'DELETE' });

export const listAdminCommands = () =>
  apiFetch<{ commands: SlashCommand[] }>('/api/admin/commands').then((r) => r.commands);

export const createCommand = (input: CreateSlashCommandInput) =>
  post<{ command: SlashCommand; signingSecret: string }>('/api/admin/commands', input);

export const removeCommand = (id: string) =>
  apiFetch<{ ok: boolean }>(`/api/admin/commands/${id}`, { method: 'DELETE' });

export const listOutgoingWebhooks = () =>
  apiFetch<{ webhooks: OutgoingWebhook[] }>('/api/admin/outgoing').then((r) => r.webhooks);

export const createOutgoingWebhook = (input: CreateOutgoingWebhookInput) =>
  post<{ webhook: OutgoingWebhook; signingSecret: string }>('/api/admin/outgoing', input);

export const setOutgoingActive = (id: string, isActive: boolean) =>
  patch<{ ok: boolean }>(`/api/admin/outgoing/${id}`, { isActive });

export const updateOutgoingWebhook = (id: string, input: UpdateOutgoingWebhookInput) =>
  patch<{ ok: boolean }>(`/api/admin/outgoing/${id}`, input);

export const removeOutgoingWebhook = (id: string) =>
  apiFetch<{ ok: boolean }>(`/api/admin/outgoing/${id}`, { method: 'DELETE' });

export const listAudit = (limit = 100) =>
  apiFetch<{ entries: AuditEntry[] }>(`/api/admin/audit?limit=${limit}`).then((r) => r.entries);

export const getRetention = () =>
  apiFetch<{ policies: RetentionPolicy[] }>('/api/admin/retention').then((r) => r.policies);

export const saveRetention = (input: UpdateRetentionInput) =>
  apiFetch<{ ok: boolean }>('/api/admin/retention', {
    method: 'PUT',
    body: JSON.stringify(input),
  });

// ─── Web Push ─────────────────────────────────────────────────────────────────

export const getPushKey = () =>
  apiFetch<{ publicKey: string | null }>('/api/push/key').then((r) => r.publicKey);

export const getPushStatus = () =>
  apiFetch<{ enabled: boolean; devices: number }>('/api/push/status');

export const subscribePush = (subscription: PushSubscriptionJSON) =>
  post<{ ok: boolean }>('/api/push/subscribe', subscription);

export const unsubscribePush = (endpoint: string) =>
  post<{ ok: boolean }>('/api/push/unsubscribe', { endpoint });

// ─── Huddle ───────────────────────────────────────────────────────────────────

export const listHuddles = () =>
  apiFetch<{ huddles: Huddle[] }>('/api/huddles').then((r) => r.huddles);

export const huddlePreflight = () =>
  apiFetch<{ ok: boolean; detail: string | null; url: string | null }>('/api/huddles/preflight');

/** A csatorna elo olelese, vagy `null`. A hivas-ablak ebbol tudja, el-e meg. */
export const getChannelHuddle = (channelId: string) =>
  apiFetch<{ huddle: Huddle | null }>(`/api/channels/${channelId}/huddle`).then((r) => r.huddle);

export const startHuddle = (
  channelId: string,
  input: { sessionId: string; title?: string; answer?: boolean },
) =>
  post<{ huddle: Huddle; token: HuddleToken; created: boolean }>(
    `/api/channels/${channelId}/huddle`,
    input,
  );

export const refreshHuddleToken = (huddleId: string) =>
  post<{ token: HuddleToken }>(`/api/huddles/${huddleId}/token`).then((r) => r.token);

export const huddleHeartbeat = (
  huddleId: string,
  input: { sessionId: string; isMuted?: boolean; isSharingScreen?: boolean },
) => post<{ huddle: Huddle }>(`/api/huddles/${huddleId}/heartbeat`, input).then((r) => r.huddle);

export const leaveHuddleApi = (huddleId: string, sessionId: string) =>
  post<{ ok: boolean; ended: boolean }>(`/api/huddles/${huddleId}/leave`, { sessionId });

// ─── Telefon (SIP) ────────────────────────────────────────────────────────────

/** A hivas telefonos aganak bontasa ("Telefon bontása"). */
export const hangUpPhoneLeg = (huddleId: string, legId: string) =>
  post<{ ok: boolean }>(`/api/huddles/${huddleId}/phone/${legId}/hangup`, {});

/** A telefonhivas atadasa egy kollegának - amikor belep, mi kilepunk. */
export const transferCall = (huddleId: string, userId: string) =>
  post<{ ok: boolean }>(`/api/huddles/${huddleId}/transfer`, { userId });

/** Kimeno hivas a huddle-bol. A jogot es a szamszabalyt a szerver donti el. */
export const dialPhone = (huddleId: string, number: string, lineId?: string) =>
  post<{ leg: PhoneLeg }>(`/api/huddles/${huddleId}/phone/dial`, {
    number,
    ...(lineId ? { lineId } : {}),
  });

/** A vonalak (szamok), amikrol en hivhatok kifele. */
export const listCallableLines = () =>
  apiFetch<{ lines: CallableLine[] }>('/api/phone/lines/callable').then((r) => r.lines);

/** Probahivas a PBX-en at (szuperadmin) - a kimenetelt varja meg. */
export const testPhoneCall = (number: string, lineId?: string) =>
  post<{
    result: { ok: boolean; detail: string; sipStatusCode: number | null; dialed: string };
  }>('/api/admin/phone/test-call', { number, ...(lineId ? { lineId } : {}) }).then((r) => r.result);

// ─── Telefonos csatorna es cimjegyzek ────────────────────────────────────────

export const listMyPhoneLines = () =>
  apiFetch<{ lines: MyPhoneLine[] }>('/api/phone/lines/mine').then((r) => r.lines);

export const getPhoneContacts = () =>
  apiFetch<{ contacts: PhoneContact[] }>('/api/phone/contacts').then((r) => r.contacts);

export const getPhoneRecent = () =>
  apiFetch<{ recent: PhoneRecent[] }>('/api/phone/recent').then((r) => r.recent);

/** Nev egy szamhoz; ures nev, cegnev es TT-ugyfel egyutt = torles. */
export const savePhoneContact = (
  number: string,
  name: string,
  extra: { companyName?: string; ttClinicId?: string } = {},
) =>
  apiFetch<{ contacts: PhoneContact[] }>('/api/phone/contacts', {
    method: 'PUT',
    body: JSON.stringify({ number, name, ...extra }),
  }).then((r) => r.contacts);

/** KI HIV? - a TT ugyfelkartyaja egy szamhoz. */
export const identifyPhoneNumber = (number: string) =>
  apiFetch<PhoneIdentity>(`/api/phone/identify?number=${encodeURIComponent(number)}`);

/** Egy uzenet korabbi valtozatai - a "(szerkesztve)" jelzes mogott. */
export const getMessageRevisions = (messageId: string) =>
  apiFetch<{ revisions: MessageRevision[] }>(`/api/messages/${messageId}/revisions`).then(
    (r) => r.revisions,
  );

/**
 * Hivasjegyzet mentese - a kolléga ugyfelrendszere szerint a TT-be, a
 * ProjectFlow-feladatra, vagy csak az AVSlackbe.
 */
export const sendCallNote = (huddleId: string, text: string, tema?: string) =>
  post<CallNoteResult>(`/api/huddles/${huddleId}/call-note`, { text, ...(tema ? { tema } : {}) });

/** A mentett hivasjegyzet, es hova menne a mentes. */
export const getCallNote = (huddleId: string) =>
  apiFetch<CallNoteState>(`/api/huddles/${huddleId}/call-note`);

/** Egy TT-ugyfel telefonszamai - a cimjegyzekben kinyitva. */
export const getTtNumbers = (clinicId: string) =>
  apiFetch<{ numbers: TtNumber[] }>(
    `/api/phone/tt-numbers?clinicId=${encodeURIComponent(clinicId)}`,
  ).then((r) => r.numbers);

/** Keresés a TT-ben, hogy egy ismeretlen szamot ugyfelhez lehessen kotni. */
export const searchTtCustomers = (q: string) =>
  apiFetch<{ ttDown: boolean; matches: TtMatch[] }>(
    `/api/phone/tt-search?q=${encodeURIComponent(q)}`,
  );

export const getSipSettings = () =>
  apiFetch<{ settings: SipSettings }>('/api/admin/phone/settings').then((r) => r.settings);

export const saveSipSettings = (input: SipSettingsInput) =>
  apiFetch<{ settings: SipSettings }>('/api/admin/phone/settings', {
    method: 'PUT',
    body: JSON.stringify(input),
  }).then((r) => r.settings);

export const listPhoneLines = () =>
  apiFetch<{ lines: PhoneLine[] }>('/api/admin/phone/lines').then((r) => r.lines);

/** Egy ceg kollegai es csatornai a vonal-urlaphoz (a csapat-adminnal mindig a sajat cege). */
export const listPhoneLineCandidates = (teamId: string) =>
  apiFetch<PhoneLineCandidates>(`/api/admin/phone/candidates?teamId=${encodeURIComponent(teamId)}`);

export const createPhoneLine = (input: PhoneLineInput) =>
  post<{ line: PhoneLine }>('/api/admin/phone/lines', input).then((r) => r.line);

export const updatePhoneLine = (id: string, input: PhoneLineInput) =>
  apiFetch<{ line: PhoneLine }>(`/api/admin/phone/lines/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  }).then((r) => r.line);

export const deletePhoneLine = (id: string) =>
  apiFetch<{ ok: boolean }>(`/api/admin/phone/lines/${id}`, { method: 'DELETE' });

/** BEHIVAS VISSZAVONASA: a hivott meg nem vette fel. */
export const cancelHuddleInvite = (huddleId: string, userId: string) =>
  post<{ ok: true; cancelled: boolean; huddle: Huddle }>(`/api/huddles/${huddleId}/invite/cancel`, {
    userId,
  });

export const inviteToHuddle = (huddleId: string, userIds: string[]) =>
  post<{ ok: boolean; invited: string[]; huddle: Huddle }>(`/api/huddles/${huddleId}/invite`, {
    userIds,
  });

/**
 * A hivott nemet mond.
 *
 * Csatorna szerint kerdezzuk le a hivast, mert a hivott ablaka nem feltetlenul
 * ismeri a huddle azonositojat - a csorgest a csatorna alapjan kapta.
 */
/**
 * Kapcsolat nelkul elmaradt hivas bejelentese (K2) - a kapcsolat visszaterte
 * utan, a kimeno sorbol (data/outbox.ts).
 */
export const reportOfflineCall = (
  channelId: string,
  input: { clientId: string; attemptedAt: string },
) => post<{ ok: boolean; message?: Message }>(`/api/channels/${channelId}/missed-call`, input);

// ─── Vendeglink (H2) ──────────────────────────────────────────────────────────

export const createGuestLink = (channelId: string, input: CreateGuestLinkInput) =>
  post<{ link: GuestLink }>(`/api/channels/${channelId}/guest-links`, input);

export const listGuestLinks = (channelId: string) =>
  apiFetch<{ links: GuestLink[] }>(`/api/channels/${channelId}/guest-links`);

export const revokeGuestLink = (linkId: string) =>
  apiFetch<{ ok: boolean }>(`/api/guest-links/${linkId}`, { method: 'DELETE' });

export const decideHuddleGuest = (huddleId: string, guestId: string, admit: boolean) =>
  post<{ ok?: boolean }>(
    `/api/huddles/${huddleId}/guests/${guestId}/${admit ? 'admit' : 'reject'}`,
  );

export const setHuddleGuestPermissions = (
  huddleId: string,
  guestId: string,
  permissions: GuestPermission[],
) =>
  post<{ guest: HuddleGuest }>(`/api/huddles/${huddleId}/guests/${guestId}/permissions`, {
    permissions,
  });

export const removeHuddleGuest = (huddleId: string, guestId: string) =>
  post<{ ok: boolean }>(`/api/huddles/${huddleId}/guests/${guestId}/remove`);

export const declineHuddle = async (channelId: string) => {
  const huddle = await getChannelHuddle(channelId);
  if (!huddle) return;
  await post<{ ok: boolean }>(`/api/huddles/${huddle.id}/decline`);
};

export const muteHuddleParticipant = (huddleId: string, userId: string) =>
  post<{ ok: boolean }>(`/api/huddles/${huddleId}/mute`, { userId });

/**
 * Az olelesben valtott uzenetek.
 *
 * CSAK a hivas uzenetei jonnek vissza, a csatorna elozmenye nem - igy a
 * behivott vendeg sem lat bele a csatornaba.
 */
export const listHuddleChat = (huddleId: string) =>
  apiFetch<{ messages: Message[] }>(`/api/huddles/${huddleId}/chat`).then((r) => r.messages);

export const sendHuddleChat = (huddleId: string, input: HuddleChatInput) =>
  post<{ message: Message }>(`/api/huddles/${huddleId}/chat`, input).then((r) => r.message);

// ─── Atirat ───────────────────────────────────────────────────────────────────

export const setHuddleTranscribe = (huddleId: string, enabled: boolean) =>
  post<{ ok: boolean; transcribe: boolean }>(`/api/huddles/${huddleId}/transcribe`, { enabled });

/** Hazigazda-atadas (kiscsoportos szobak) - csak a jelenlegi hazigazda. */
export const setHuddleHost = (huddleId: string, userId: string) =>
  post<{ ok: boolean }>(`/api/huddles/${huddleId}/host`, { userId });

/** Kiscsoportos idoszak inditasa - csak a hazigazda. */
export const startBreakouts = (huddleId: string, input: StartBreakoutInput) =>
  post<{ huddle: Huddle | null }>(`/api/huddles/${huddleId}/breakouts`, input);

/** Hosszabbitas es/vagy egy ember athelyezese a nyitott idoszakban. */
export const updateBreakouts = (huddleId: string, input: UpdateBreakoutInput) =>
  patch<{ huddle: Huddle | null }>(`/api/huddles/${huddleId}/breakouts/current`, input);

// ─── Vevoszolgalati beallitas (docs/20) ───────────────────────────────────────

/** A SAJAT beallitasom: kesz ertek, zarak, ceg- es globalis szint. */
export const getMyService = () => apiFetch<{ status: ServiceStatus }>('/api/me/service');

/** Sajat ertekek szerkesztese; `null` = vissza az oroklesre. A zart mezokre 403 jon. */
export const putMyService = (input: UpdateServiceFieldsInput) =>
  put<{ status: ServiceStatus }>('/api/me/service', input);

/** Admin: a (lathato) cegek es kollegak, a kesz ertekekkel. */
export const getAdminService = () =>
  apiFetch<{ overview: AdminServiceOverview }>('/api/admin/service');

/** ADMIN: egy felhasznalo csatornai az ertesites-diagnosztikahoz. */
export const getNotificationChannels = (userId: string) =>
  apiFetch<{ channels: { id: string; name: string | null; kind: string }[] }>(
    `/api/admin/notifications/channels?userId=${encodeURIComponent(userId)}`,
  );

/** ADMIN: miert (nem) kapna ertesitest a felhasznalo ebben a csatornaban. */
export const getNotificationDiagnosis = (userId: string, channelId: string) =>
  apiFetch<{ diagnosis: NotificationDiagnosis }>(
    `/api/admin/notifications/diagnose?userId=${encodeURIComponent(userId)}&channelId=${encodeURIComponent(channelId)}`,
  );

/** Szuperadmin: a globalis alapertek. */
export const putGlobalService = (input: UpdateServiceLevelInput) =>
  put<{ level: ServiceLevel }>('/api/admin/service/global', input);

export const putTeamService = (teamId: string, input: UpdateServiceLevelInput) =>
  put<{ level: ServiceLevel }>(`/api/admin/service/teams/${teamId}`, input);

export const putUserService = (userId: string, input: UpdateServiceFieldsInput) =>
  put<{ own: ServiceFields }>(`/api/admin/service/users/${userId}`, input);

/** SZAVAZAS az olelesben: inditas. A megoszlas a huddle pillanatkepeben jon vissza. */
export const startHuddlePoll = (huddleId: string, input: CreateHuddlePollInput) =>
  post<{ poll: HuddlePoll | null }>(`/api/huddles/${huddleId}/poll`, input);

/** A nyitott szavazas a SAJAT szavazatainkkal. */
export const getHuddlePoll = (huddleId: string) =>
  apiFetch<{ poll: HuddlePoll | null; myVotes: string[] }>(`/api/huddles/${huddleId}/poll`);

/** Szavazat; ures lista visszavonja. */
export const voteHuddlePoll = (huddleId: string, optionIds: string[]) =>
  post<{ poll: HuddlePoll | null; myVotes: string[] }>(`/api/huddles/${huddleId}/poll/vote`, {
    optionIds,
  });

/** Lezaras: az eredmeny a csatorna folyamaba kerul. */
export const closeHuddlePoll = (huddleId: string) =>
  post<{ ok: boolean }>(`/api/huddles/${huddleId}/poll/close`, {});

/** KOZOS stopper/idozito az olelesben: inditas, szunet, folytatas, nullazas, eltuntetes. */
export const huddleTimerAction = (huddleId: string, input: TimerActionInput) =>
  post<{ huddle: Huddle | null }>(`/api/huddles/${huddleId}/timer`, input);

/** A hazigazda megnyitja (vagy visszazarja) a stopper kezeleset a tobbiek elott. */
export const setHuddleTimerAccess = (huddleId: string, open: boolean) =>
  post<{ huddle: Huddle | null }>(`/api/huddles/${huddleId}/timer/access`, { open });

/** Korai lezaras: mindenki visszater a kozos beszelgetesbe. */
export const closeBreakouts = (huddleId: string) =>
  post<{ huddle: Huddle | null }>(`/api/huddles/${huddleId}/breakouts/close`, {});

/**
 * Hely a kovetkezo hangdarabnak.
 *
 * A hangbajtok NEM a szerveren mennek at: a bongeszo kozvetlenul a tarolóba
 * tolt fel erre a rovid eletu cimre. A kulcsot a szerver adja.
 */
export const requestTranscriptChunkUrl = (
  huddleId: string,
  input: { startMs: number; durationMs: number; mimeType: string; phoneLegId?: string },
) =>
  post<{ key: string; uploadUrl: string }>(`/api/huddles/${huddleId}/transcript/chunk-url`, input);

export const completeTranscriptChunk = (
  huddleId: string,
  input: { startMs: number; durationMs: number; mimeType: string; phoneLegId?: string },
) => post<{ ok: boolean; queued: boolean }>(`/api/huddles/${huddleId}/transcript/chunk`, input);

export const getTranscript = (huddleId: string) =>
  apiFetch<{ transcript: Transcript | null }>(`/api/huddles/${huddleId}/transcript`).then(
    (r) => r.transcript,
  );

export const shareTranscript = (huddleId: string, userIds: string[]) =>
  post<{ transcript: Transcript }>(`/api/huddles/${huddleId}/transcript/share`, {
    userIds,
  }).then((r) => r.transcript);

/**
 * Tobb hivas atirat-allapota, egy kerdesben.
 *
 * Ebbol dol el, hol jelenjen meg az "Atirat" gomb a folyamban. Amihez nincs
 * jogunk, az egyszeruen nem szerepel a valaszban.
 */
// ─── Nyelvi modell ────────────────────────────────────────────────────────────

export const getAiSettings = () =>
  apiFetch<{ settings: AiSettings; configured: boolean }>('/api/admin/ai');

export const saveAiSettings = (input: Partial<AiSettings>) =>
  apiFetch<{ settings: AiSettings }>('/api/admin/ai', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input),
  }).then((r) => r.settings);

/** Be van-e kapcsolva a csatorna-osszefoglalo (a gomb csak ekkor latszik). */
export const getRecapStatus = () => apiFetch<{ enabled: boolean }>('/api/recap/status');

/** "Mi tortent, amig tavol voltal" - az olvasatlan reszbol. */
export const requestChannelRecap = (channelId: string, afterSeq: number) =>
  post<ChannelRecap>(`/api/channels/${channelId}/recap`, { afterSeq });

export const getAiModels = () =>
  apiFetch<{ models: OpenRouterModel[] }>('/api/admin/ai/models').then((r) => r.models);

export const getTranscriptStates = (huddleIds: string[]) =>
  apiFetch<{ states: Record<string, TranscriptState> }>(
    `/api/transcripts/states?huddleIds=${encodeURIComponent(huddleIds.join(','))}`,
  ).then((r) => r.states);

export const searchTranscripts = (q: string, limit = 20) =>
  apiFetch<{ hits: TranscriptHit[] }>(
    `/api/transcripts/search?q=${encodeURIComponent(q)}&limit=${limit}`,
  ).then((r) => r.hits);

/** Vaszonkereses - a keresooldal kulon szakaszahoz, `channelId`-vel a csatorna keresojehez. */
export const searchCanvases = (q: string, limit = 10, channelId?: string) =>
  apiFetch<{ hits: CanvasSearchHit[] }>(
    `/api/search/canvases?q=${encodeURIComponent(q)}&limit=${limit}` +
      (channelId ? `&channelId=${encodeURIComponent(channelId)}` : ''),
  ).then((r) => r.hits);

export const reportHuddleEvent = (huddleId: string, input: HuddleEventInput) =>
  post<{ ok: boolean }>(`/api/huddles/${huddleId}/events`, input);

/** Csatlakozas ELOTTI hiba - ilyenkor meg nincs huddle, amihez kotni lehetne. */
export const reportPreflightFailure = (detail?: string) =>
  post<{ ok: boolean }>('/api/huddles/events', {
    kind: 'preflight_failed',
    ...(detail ? { detail } : {}),
  });

// ─── Canvas ───────────────────────────────────────────────────────────────────

/*
  Csak az ADATLAP megy REST-en (cim, kiemeles, ki mikor nyult hozza). A canvas
  tartalma sajat binaris WebSocketen (`/ws/canvas`) erkezik - lasd
  `lib/canvas-sync.ts`.
*/

export const listCanvases = (channelId: string) =>
  apiFetch<{ canvases: Canvas[] }>(`/api/channels/${channelId}/canvases`).then((r) => r.canvases);

export const createCanvasApi = (channelId: string, input: { title: string; isPrimary?: boolean }) =>
  post<{ canvas: Canvas }>(`/api/channels/${channelId}/canvases`, input).then((r) => r.canvas);

export const updateCanvasApi = (canvasId: string, patch: { title?: string; isPrimary?: boolean }) =>
  apiFetch<{ canvas: Canvas }>(`/api/canvases/${canvasId}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  }).then((r) => r.canvas);

/** A csatorna canvasainak kozos sorrendje (huzas utan). */
export const reorderCanvasesApi = (channelId: string, canvasIds: string[]) =>
  apiFetch<{ canvasIds: string[] }>(`/api/channels/${channelId}/canvases/order`, {
    method: 'PUT',
    body: JSON.stringify({ canvasIds }),
  }).then((r) => r.canvasIds);

export const deleteCanvasApi = (canvasId: string) =>
  apiFetch<void>(`/api/canvases/${canvasId}`, { method: 'DELETE' });

/** SZEMELYES canvas megosztasai - csak a tulajdonos kerheti le. */
export const listCanvasSharesApi = (canvasId: string) =>
  apiFetch<{ shares: CanvasShare[] }>(`/api/canvases/${canvasId}/shares`).then((r) => r.shares);

export const shareCanvasApi = (
  canvasId: string,
  input: { kind: CanvasShareKind; targetId: string },
) => post<{ share: CanvasShare }>(`/api/canvases/${canvasId}/shares`, input).then((r) => r.share);

export const unshareCanvasApi = (canvasId: string, shareId: string) =>
  apiFetch<void>(`/api/canvases/${canvasId}/shares/${shareId}`, { method: 'DELETE' });

// ─── Listak ───────────────────────────────────────────────────────────────────

/**
 * Minden lista-muvelet a TELJES listaval ter vissza, a teteleivel egyutt.
 *
 * Egy tetel pipalasa az osszegzo szamokat is elmozditja ("3/8 kész"), es a
 * sorrend is valtozhat. Egyetlen valaszban rendezve ez sosem hagy felig
 * frissitett allapotot a kepernyon.
 */
export const getLists = (channelId: string) =>
  apiFetch<{ lists: List[] }>(`/api/channels/${channelId}/lists`).then((r) => r.lists);

export const createList = (channelId: string, input: { title: string; description?: string }) =>
  post<{ list: List }>(`/api/channels/${channelId}/lists`, input).then((r) => r.list);

export const updateList = (listId: string, modositas: { title?: string; description?: string }) =>
  patch<{ list: List }>(`/api/lists/${listId}`, modositas).then((r) => r.list);

export const deleteList = (listId: string) =>
  apiFetch<void>(`/api/lists/${listId}`, { method: 'DELETE' });

export const createListItem = (
  listId: string,
  input: { text?: string; assigneeId?: string | null; dueDate?: string | null },
) => post<{ list: List }>(`/api/lists/${listId}/items`, input).then((r) => r.list);

export const updateListItem = (
  itemId: string,
  modositas: {
    text?: string;
    isDone?: boolean;
    assigneeId?: string | null;
    dueDate?: string | null;
    afterItemId?: string | null;
  },
) => patch<{ list: List }>(`/api/list-items/${itemId}`, modositas).then((r) => r.list);

export const deleteListItem = (itemId: string) =>
  apiFetch<{ list: List }>(`/api/list-items/${itemId}`, { method: 'DELETE' }).then((r) => r.list);

export const attachFileToCanvas = (canvasId: string, fileId: string) =>
  post<{ file: FileAttachment }>(`/api/canvases/${canvasId}/files`, { fileId }).then((r) => r.file);

/** Ki hol tart a csatornaban - a "latta" jelzes kiindulo allapota. */
export const listReadPositions = (channelId: string) =>
  apiFetch<{ reads: { userId: string; lastReadSeq: number }[] }>(
    `/api/channels/${channelId}/reads`,
  ).then((r) => r.reads);

// ─── Atirat-meres (szuperadmin) ───────────────────────────────────────────────

export interface TranscribeTestHuddle {
  id: string;
  startedAt: string;
  endedAt: string | null;
  audioKeepUntil: string | null;
  channelName: string | null;
  channelKind: string;
  chunks: number;
}

export interface TranscribeChunkInfo {
  id: string;
  startMs: number;
  durationMs: number;
  bytes: number;
  mimeType: string;
  state: string;
  hasAudio: boolean;
  speakerKey: string;
  speakerName: string;
}

export type TranscribeEvalVariant =
  | { kind: 'current' }
  | {
      kind: 'rerun';
      provider?: 'openai' | 'openrouter' | 'google' | 'whisper';
      model?: string;
      hotwords: boolean;
    };

export interface TranscribeEvalResult {
  metrics: {
    wer: number;
    cer: number;
    words: { substitutions: number; deletions: number; insertions: number; reference: number };
    hypothesisWords: number;
    invented: { word: string; count: number }[];
    missing: string[];
    numbers: { value: string; found: boolean }[];
    terms: { term: string; found: boolean }[];
  };
  hypothesis: string;
  chunks: {
    chunkId: string;
    startMs: number;
    speakerName: string;
    text: string | null;
    dropped: number;
    error?: string;
  }[];
  failed: number;
  dropped: number;
  elapsedMs: number;
}

export const setTestHuddle = (huddleId: string, enabled: boolean) =>
  post<{ huddle: Huddle }>(`/api/admin/transcribe/test-huddle/${huddleId}`, { enabled });

export const listTranscribeTestHuddles = () =>
  apiFetch<{ huddles: TranscribeTestHuddle[] }>('/api/admin/transcribe/test-huddles').then(
    (r) => r.huddles,
  );

export const listTranscribeChunks = (huddleId: string) =>
  apiFetch<{ chunks: TranscribeChunkInfo[] }>(
    `/api/admin/transcribe/huddles/${huddleId}/chunks`,
  ).then((r) => r.chunks);

export const transcribeChunkAudioUrl = (chunkId: string) =>
  `/api/admin/transcribe/chunks/${chunkId}/audio`;

export const runTranscribeEval = (input: {
  huddleId: string;
  reference: string;
  terms: string[];
  variant: TranscribeEvalVariant;
  speakers: string[];
  fromMs?: number;
  toMs?: number;
}) => post<TranscribeEvalResult>('/api/admin/transcribe/eval', input);

// ─── Telefonos statisztika ────────────────────────────────────────────────────

export const getPhoneStats = (
  channelId: string,
  query: { from: string; to: string; users?: string[]; direction?: 'all' | 'inbound' | 'outbound' },
) => {
  const params = new URLSearchParams({ from: query.from, to: query.to });
  if (query.users?.length) params.set('users', query.users.join(','));
  if (query.direction && query.direction !== 'all') params.set('direction', query.direction);
  return apiFetch<PhoneStats>(`/api/channels/${channelId}/phone-stats?${params.toString()}`);
};
